import React, { useRef, useState } from "react";
import { AutoComplete, Flex, Input, InputNumber, Typography } from "antd";
import { AimOutlined, SearchOutlined } from "@ant-design/icons";

import LeafletMap from "../ext/LeafletMap";
import { POINTS_ADMIN } from "../../../config/lang";
import { FILTER_DEBOUNCE_MS } from "../../../config/config";
import { translate } from "../../../util/util";

/* Real address lookup: Nominatim `search`, a handful of candidates the user picks from — not a
   blind jump to the single top hit. Debounced and abortable, both because a keystroke-per-request
   flood is wrong and because Nominatim's usage policy is ~1 req/s (see the memory note). */
async function geocodeSearch(query, signal) {
  const url = `https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&limit=6&q=${encodeURIComponent(query)}`;
  const res = await fetch(url, { signal, headers: { "Accept-Language": "en" } });
  return res.ok ? res.json() : [];
}

async function geocodeReverse(lat, lng) {
  const url = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`;
  const res = await fetch(url);
  return res.json();
}

const MIN_CHARS = 3;

export default function LocationPickerField({ field, value, onChange, item, labels = {}, lang }) {
  const t = key => translate(POINTS_ADMIN, key, lang);
  const [text, setText] = useState((item && item.address) || "");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  const debounce = useRef(null);
  const controller = useRef(null);

  const lat = value ? value.latitude : null;
  const lng = value ? value.longitude : null;

  /* Type → debounced fetch → candidate list. One AbortController per request, so a fast typist
     leaves one request in flight rather than one per keystroke. */
  const handleSearch = query => {
    setText(query);
    clearTimeout(debounce.current);
    if (controller.current) controller.current.abort();
    if (!query || query.trim().length < MIN_CHARS) {
      setOptions([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    debounce.current = setTimeout(() => {
      controller.current = new AbortController();
      geocodeSearch(query.trim(), controller.current.signal)
        .then(results => {
          setOptions(
            (results || []).map(r => ({
              value: r.place_id ? String(r.place_id) : r.display_name,
              label: r.display_name,
              result: r,
            }))
          );
          setLoading(false);
        })
        .catch(error => {
          if (error.name === "AbortError") return;
          setOptions([]);
          setLoading(false);
        });
    }, FILTER_DEBOUNCE_MS);
  };

  /* Picking a candidate is the single writer of a searched location: it sets the coordinate AND
     the address together, and pans the map (value change drives LeafletMap). */
  const handleSelect = (optionValue, option) => {
    const r = option.result;
    if (!r) return;
    setText(r.display_name);
    setOptions([]);
    onChange(field.name, { latitude: Number(r.lat), longitude: Number(r.lon) });
    onChange("address", r.display_name);
  };

  const reverseGeocode = async (rLat, rLng) => {
    try {
      const result = await geocodeReverse(rLat, rLng);
      if (result && result.display_name) {
        setText(result.display_name);
        onChange("address", result.display_name);
      }
    } catch (e) {
      // ignore reverse geocode failures, never crash the form
    }
  };

  return (
    <Flex vertical gap="small">
      <AutoComplete
        options={options}
        value={text}
        onSearch={handleSearch}
        onSelect={handleSelect}
        onChange={setText}
        /* Keep the picked/typed text visible; matching is server-side, so don't also filter the
           returned candidates client-side. */
        filterOption={false}
        notFoundContent={loading ? t("searching") : text.trim().length >= MIN_CHARS ? t("not_found") : t("address_min_chars")}>
        <Input allowClear prefix={<SearchOutlined />} placeholder={t("search_address")} aria-label={t("search_address")} />
      </AutoComplete>

      {/* Coordinates are a read-out, not an entry field: they are set by the address lookup above
          or by clicking the map below, so hand-editing them (and desyncing them from the chosen
          address) is disabled. */}
      <Flex gap="small">
        <InputNumber prefix={<AimOutlined />} style={{ flex: 1 }} value={lat ?? null} placeholder={labels.lat} aria-label={labels.lat} disabled />
        <InputNumber prefix={<AimOutlined />} style={{ flex: 1 }} value={lng ?? null} placeholder={labels.lng} aria-label={labels.lng} disabled />
      </Flex>
      <Typography.Text type="secondary" className="pt-loc-hint">
        {t("coords_readonly")}
      </Typography.Text>

      <LeafletMap
        lat={value && value.latitude}
        lng={value && value.longitude}
        zoom={value ? 15 : 6}
        editable
        onChange={({ lat: newLat, lng: newLng }) => {
          onChange(field.name, { latitude: newLat, longitude: newLng });
          reverseGeocode(newLat, newLng);
        }}
        height={300}
      />
    </Flex>
  );
}
